import { useMemo } from 'react';

import { OccupationType } from 'api/types/entity';
import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  DebtsSection,
  PersonalDataSection,
  CitizenshipSection,
  PlaceOfBirthSection,
  BiographySection,
  ContactsSection,
  PepConnectionSection,
  TaxResidencySection,
  BankAccountsSection,
  ActivitySection,
  AdditionalDocumentsSection,
  IdentityDocumentSection,
  KYCSection,
  RegistrationAddressSection,
  ResidentialAddressSection,
} from 'modules/client/common/components/sections';
import {
  CountryRiskBadgesProps,
  CountryRiskField,
} from 'modules/client/common/feature';
import { RepresentativeSectionsProps } from 'modules/client/representativeClient/components/RepresentativeSections/types';

import { useFormContext } from '../../forms/representativeForm';
import { BasisForRepresentation } from '../sections';

interface MainInfo {
  label: string;
  value?: string;
  countryRisk?: CountryRiskBadgesProps;
}

export const useRepresentativeSections = ({
  countries,
  countriesLoading,
  kycData,
  hideActivitySection,
  mainInfo,
}: RepresentativeSectionsProps) => {
  const { t } = useTranslation();
  const { values } = useFormContext();

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const isEntrepreneur =
    values.biography?.occupation?.value === OccupationType.entrepreneur;

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.representativeClient.sections.basic'),
          id: 'basic',
          content: (
            <>
              <PersonalDataSection enabledGender={isGlobal} />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <IdentityDocumentSection
                enabledDocuments
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
                enabledDocuments={isGlobal}
              />
              <PlaceOfBirthSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t('client.representativeClient.sections.representativeInfo'),
          id: 'representativeInfo',
          content: <BasisForRepresentation />,
          formSectionKeys: ['basisForRepresentation'],
        },
        {
          label: t('client.representativeClient.sections.contactsAndAddresses'),
          id: 'contactsAndAddresses',
          content: (
            <>
              {isFinance && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection />
            </>
          ),
          formSectionKeys: [
            'contacts',
            'registrationAddress',
            'residentialAddress',
          ],
        },
        {
          label: t('client.representativeClient.sections.documents'),
          id: 'documents',
          content: <AdditionalDocumentsSection />,
          formSectionKeys: ['additionalDocuments'],
        },
        {
          label: t('client.representativeClient.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <KYCSection kycData={kycData} />
              {isFinance && <PepConnectionSection />}
            </>
          ),
          formSectionKeys: ['kyc', 'pepConnection'],
        },
        {
          label: t('client.representativeClient.sections.other'),
          id: 'other',
          content: (
            <>
              {!hideActivitySection && <ActivitySection />}
              {isFinance && <BiographySection />}
              {isFinance && (
                <BankAccountsSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              {isFinance && (
                <DebtsSection
                  hideMarginTrades
                  isEntrepreneur={isEntrepreneur}
                />
              )}
            </>
          ),
          formSectionKeys: ['activity', 'biography', 'bankAccounts', 'debts'],
        },
      ]),
    [
      t,
      isGlobal,
      isFinance,
      countries,
      countriesLoading,
      kycData,
      hideActivitySection,
      isEntrepreneur,
    ]
  );

  const globalInfo: MainInfo[][] | undefined = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t('client.representativeClient.mainInfo.citizenship'),
                value: mainInfo.citizenship?.name,
                countryRisk: {
                  country: mainInfo.citizenship,
                  field: CountryRiskField.Citizenship,
                },
              },
              {
                label: t('client.representativeClient.mainInfo.country'),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.ResidentialAddress,
                },
              },
            ],
            [
              {
                label: t('client.representativeClient.mainInfo.phone'),
                value: mainInfo.phone,
              },
              {
                label: t('client.representativeClient.mainInfo.email'),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t('client.representativeClient.mainInfo.documentNumber'),
                value: mainInfo.basisForRepresentationDocumentNumber,
              },
            ],
            [
              {
                label: t(
                  'client.representativeClient.mainInfo.documentExpirationDate'
                ),
                value:
                  mainInfo.basisForRepresentationDocumentExpirationDate &&
                  formatToDate(
                    mainInfo.basisForRepresentationDocumentExpirationDate
                  ),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  const financeInfo: MainInfo[][] | undefined = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t('client.representativeClient.mainInfo.email'),
                value: mainInfo.email,
              },
              {
                label: t('client.representativeClient.mainInfo.phone'),
                value: mainInfo.phone,
              },
            ],
            [
              {
                label: t('client.representativeClient.mainInfo.country'),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.RegistrationAddress,
                },
              },
            ],
            [
              {
                label: t('client.representativeClient.mainInfo.documentNumber'),
                value: mainInfo.basisForRepresentationDocumentNumber,
              },
            ],
            [
              {
                label: t(
                  'client.representativeClient.mainInfo.documentExpirationDate'
                ),
                value:
                  mainInfo.basisForRepresentationDocumentExpirationDate &&
                  formatToDate(
                    mainInfo.basisForRepresentationDocumentExpirationDate
                  ),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  const info = isGlobal ? globalInfo : financeInfo;

  return { tabs, info };
};
